import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  iTicketingSetup,
  iTicketType,
} from 'services/Ticketing/Ticketing.type';
import {
  card,
  CategoryChip,
  formatMoney,
  formatShortDay,
  primaryButton,
  sectionTitle,
} from '../common';

interface TicketTypesSectionProps {
  data: iTicketingSetup;
  setEditing: (type: Partial<iTicketType> | null) => void;
  preview: (typeId?: number) => void;
}

export function TicketTypesSection({
  data,
  setEditing,
  preview,
}: TicketTypesSectionProps): JSX.Element {
  const s = data.settings;

  return (
    <Box sx={{ ...card, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography sx={sectionTitle}>Types de billets</Typography>
          <Typography sx={{ fontSize: 13, color: '#666' }}>
            Pass 1 jour ou plusieurs jours, exposants, montage / démontage,
            organisateurs. « Sur invitation » = non proposé au public.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={primaryButton}
          onClick={() =>
            setEditing({
              category: 'visitor',
              name_fr: 'Pass visiteur',
              price_cents: 0,
              quota: null,
              valid_days: [],
              allow_reentry: true,
              is_public: true,
              is_active: true,
            })
          }
        >
          Créer des billets
        </Button>
      </Box>

      {data.types.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#888' }}>
          Aucun type de billet pour le moment : cliquez sur « Créer des billets
          ».
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Billet</TableCell>
              <TableCell>Prix TTC</TableCell>
              <TableCell>Jours</TableCell>
              <TableCell>Émis / quota</TableCell>
              <TableCell>Accès</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.types.map((t) => (
              <TableRow key={t.id} sx={{ opacity: t.is_active ? 1 : 0.5 }}>
                <TableCell>
                  <CategoryChip category={t.category} />{' '}
                  <b style={{ marginLeft: 6 }}>{t.name_fr}</b>
                </TableCell>
                <TableCell>
                  {t.price_cents
                    ? formatMoney(t.price_cents, s.currency)
                    : 'Gratuit'}
                </TableCell>
                <TableCell sx={{ color: '#555' }}>
                  {t.valid_days.length
                    ? t.valid_days.map((d) => formatShortDay(d)).join(', ')
                    : 'Tous les jours du salon'}
                </TableCell>
                <TableCell>
                  {t.sold}
                  {t.quota ? ` / ${t.quota}` : ''}
                </TableCell>
                <TableCell sx={{ color: '#555' }}>
                  {t.is_public ? 'Public' : 'Sur invitation'}
                  {t.allow_reentry ? '' : ' · entrée unique'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button
                    size="small"
                    sx={{ textTransform: 'none' }}
                    onClick={() => setEditing(t)}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="small"
                    sx={{ textTransform: 'none', color: '#555' }}
                    onClick={() => preview(t.id)}
                  >
                    Aperçu PDF
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

export default TicketTypesSection;
